import { useState } from 'react';

import type { SoldSegment } from '../../lib/types';

/** The detail line says "No data" wherever the schedule has nothing to report. */
const orNoData = (value: string | undefined | null) => (value && value.trim() !== '' ? value : 'No data');

/** "+1" after the arrival clock, and nothing for a same-day arrival. */
function dayChangeMark(days: number): string {
  if (days === 0) return '';
  return days > 0 ? `+${days}` : `${days}`;
}

/** One `Label: value` pair on the detail line. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <span className="sold__fact">
      {label}: {value}
    </span>
  );
}

export function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" className={open ? 'sold__chevron--open' : ''}>
      <path d="M3 6l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** The caret on the Modify and Pricing Options menus. */
export function Caret() {
  return (
    <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
      <path d="M1 7.5h8L5 2.5z" fill="currentColor" />
    </svg>
  );
}

/** The bag the host draws for baggage: on the Air Extras tile and the fare line. */
export function BagIcon({ size = 24 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path d="M9 3.5h6a1 1 0 0 1 1 1V7h-1.6V5.1H9.6V7H8V4.5a1 1 0 0 1 1-1z" fill="currentColor" />
      <rect x="4" y="7" width="16" height="13.5" rx="1.6" fill="currentColor" />
      <path d="M8 8v11.5M16 8v11.5" stroke="var(--bag-strap, var(--sedl-white))" strokeWidth="1.2" />
    </svg>
  );
}

/** The pet and the bag the host draws on the Air Extras tile. */
export function AirExtrasIcons() {
  return (
    <span className="sold__tile-icons" aria-label="Pets and baggage">
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <ellipse cx="6" cy="9.5" rx="2.1" ry="2.6" fill="currentColor" />
        <ellipse cx="18" cy="9.5" rx="2.1" ry="2.6" fill="currentColor" />
        <ellipse cx="9.4" cy="5.2" rx="2.1" ry="2.6" fill="currentColor" />
        <ellipse cx="14.6" cy="5.2" rx="2.1" ry="2.6" fill="currentColor" />
        <path d="M12 11c-3 0-6.4 4.3-6.4 6.8 0 1.9 1.6 2.6 3.2 2.2 1.2-.3 2.1-.7 3.2-.7s2 .4 3.2.7c1.6.4 3.2-.3 3.2-2.2 0-2.5-3.4-6.8-6.4-6.8z" fill="currentColor" />
      </svg>
      <BagIcon />
    </span>
  );
}

interface SoldRowProps {
  segment: SoldSegment;
  expanded: boolean;
  onToggle: () => void;
  onReturnAvailability: () => void;
}

function SoldRow({ segment: s, expanded, onToggle, onReturnAvailability }: SoldRowProps) {
  const route = `${s.origin}-${s.destination}`;

  return (
    <div className={`sold__group${expanded ? ' sold__group--open' : ''}`}>
      <button
        type="button"
        className="sold__row"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Hide' : 'Show'} details for segment ${s.line}, ${s.carrier} ${s.number} ${route}`}
        onClick={onToggle}
      >
        <span className="sold__line">{s.line}</span>
        <span>{s.carrier}</span>
        <span>{s.number}</span>
        <span>{s.class}</span>
        <span>
          {s.date} {s.dow}
        </span>
        <span>
          {s.origin} {s.destination}
        </span>
        <span>
          {s.status}
          {s.seats}
        </span>
        <span>{s.depart}</span>
        <span>
          {s.arrive}
          {s.dayChange !== 0 && <span className="sold__daychange">{dayChangeMark(s.dayChange)}</span>}
        </span>
        <span aria-hidden="true" />
        <span className="sold__expand">
          <Chevron open={expanded} />
        </span>
      </button>

      {expanded && (
        <div className="sold__panel">
          <div className="sold__title">
            <span className="sold__route">{route}:</span>
            <span className="sold__badge">
              {s.carrier} {s.number}
            </span>
          </div>

          <div className="sold__facts">
            <Fact label="From" value={`${s.origin} ${s.date} at ${s.depart}`} />
            <Fact
              label="To"
              value={`${s.destination} ${s.arriveDate} at ${s.arrive}${s.dayChange ? ` (${dayChangeMark(s.dayChange)})` : ''}`}
            />
            {/* The segment line is in caps; the detail is in sentence case. */}
            <Fact label="Flight Time" value={orNoData(s.duration?.toLowerCase())} />
            <Fact label="Meals" value={orNoData(s.meals?.join(', '))} />
            <Fact label="Equipment" value={orNoData(s.equipName?.toUpperCase())} />
            <Fact label="Operates" value={orNoData(s.operates?.join(', '))} />
            <Fact label="On-Time" value={orNoData(s.onTime)} />
            <Fact label="Air Miles" value={s.airMiles ? String(s.airMiles) : 'No data'} />
            <Fact label="Time Diff" value={orNoData(s.timeDiff)} />
            <Fact label="Connectivity" value={orNoData(s.connectivity)} />
            <Fact label="E-ticket" value={s.eTicket ? 'Yes' : 'No'} />
          </div>

          {/* The ancillary tiles are drawn as the host draws them; none of the
              services behind them exist here yet. */}
          <div className="sold__tiles">
            <div className="sold__tile">
              <span className="sold__tile-label">Branded Fares</span>
              <span className="sold__tile-value">No Branded Fares</span>
            </div>
            <div className="sold__tile">
              <span className="sold__tile-label">Seat Map</span>
              <span className="sold__tile-value">Seat Map</span>
            </div>
            <div className="sold__tile">
              <span className="sold__tile-label">Air Extras</span>
              <AirExtrasIcons />
            </div>
            <div className="sold__tile">
              <span className="sold__tile-label">Amenities</span>
              <span className="sold__tile-value">Amenities</span>
            </div>
          </div>

          <div className="sold__actions">
            <button type="button" className="sold__button" onClick={onReturnAvailability}>
              Return Availability
            </button>
            <span className="sold__actions-spacer" />
            <button type="button" className="sold__button" title="Not implemented" disabled>
              Modify <Caret />
            </button>
            <button type="button" className="sold__button sold__button--primary" title="Not implemented" disabled>
              Pricing Options <Caret />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface SellDisplayProps {
  /** Only the segments the sell entry added, numbered as they sit in the PNR. */
  sold: SoldSegment[];
  /** Redisplays the availability the segments were sold from. */
  onReturnAvailability: () => void;
}

/** The host's reply to a sell: a booking status heading and the new segments. */
export function SellDisplay({ sold, onReturnAvailability }: SellDisplayProps) {
  const [openLine, setOpenLine] = useState<number | null>(null);

  return (
    <div className="sold">
      <div className="sold__heading">Booking Status: Segments Added to PNR</div>
      {sold.map((segment) => (
        <SoldRow
          key={segment.line}
          segment={segment}
          expanded={openLine === segment.line}
          onToggle={() => setOpenLine((open) => (open === segment.line ? null : segment.line))}
          onReturnAvailability={onReturnAvailability}
        />
      ))}
    </div>
  );
}
